/* eslint-disable camelcase */
import { z } from 'zod'

import { OTHER_VERSIONS } from './versionTail'

import {
  type AttributionDimension,
  attributionDimensions,
  type AttributionFilters,
  facetDimensions,
} from '@/api/attributionObservability'
import {
  GetMeetingObservabilityAxBucketEnum,
  GetMeetingObservabilityCalendarEventEnum,
  GetMeetingObservabilityClientEnum,
  GetMeetingObservabilityOsEnum,
  GetMeetingObservabilityParticipantBucketEnum,
  GetMeetingObservabilityParticipantSourceEnum,
  GetMeetingObservabilityPlatformEnum,
  GetMeetingObservabilityRecordingLengthBucketEnum,
  GetMeetingObservabilityUserMeetingBucketEnum,
} from '@/api/generated/api'

export const filterDimensions = attributionDimensions

export const dimensionLabels: Record<AttributionDimension, string> = {
  platform: 'Platform',
  os: 'Recorder OS',
  client: 'Client',
  calendar_event: 'Calendar event',
  participant_bucket: 'Participant estimate',
  user_meeting_bucket: 'User meeting number',
  ax_bucket: 'AX evidence coverage',
  recording_length_bucket: 'Recording length',
  backend_version: 'Backend version',
  app_version: 'App version',
}

const valueLabels: Record<string, string> = {
  unknown: 'Unknown',
  not_applicable: 'Not applicable',
  in_person: 'In person',
  google_meet: 'Google Meet',
  teams: 'Teams',
  slack: 'Slack',
  zoom: 'Zoom',
  macos: 'macOS',
  windows: 'Windows',
  ios: 'iOS',
  chrome: 'Chrome',
  edge: 'Edge',
  brave: 'Brave',
  native: 'Native',
  other: 'Other',
  mixed: 'Mixed',
}

export const formatValue = (value: string | number | boolean | null): string => {
  if (value === null || value === '') return 'Unknown'
  const text = String(value)
  return Object.hasOwn(valueLabels, text)
    ? valueLabels[text]
    : text.replaceAll('_', ' ').replace(/^./, (letter) => letter.toUpperCase())
}

export const percentage = (numerator: number | null, denominator: number | null): number | null =>
  numerator !== null && denominator !== null && denominator > 0 ? (numerator / denominator) * 100 : null

export const formatPercent = (numerator: number | null, denominator: number | null): string => {
  const value = percentage(numerator, denominator)
  return value === null ? 'Not measured' : `${value.toFixed(1)}%`
}

export const formatCount = (value: number) => value.toLocaleString('en-US')
export const formatDuration = (milliseconds: number) =>
  `${(milliseconds / 60_000).toLocaleString('en-US', { maximumFractionDigits: 1 })} min`

const DAY_MS = 86_400_000
const dateString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const date = new Date(`${value}T00:00:00Z`)
    return Number.isFinite(date.getTime()) && date.toISOString().startsWith(value)
  }, 'Choose a valid date.')

const rangeEndpoint = dateString.or(z.string().datetime())

const closedFilters = {
  platform: z.nativeEnum(GetMeetingObservabilityPlatformEnum),
  os: z.nativeEnum(GetMeetingObservabilityOsEnum),
  client: z.nativeEnum(GetMeetingObservabilityClientEnum),
  calendar_event: z.nativeEnum(GetMeetingObservabilityCalendarEventEnum),
  participant_bucket: z.nativeEnum(GetMeetingObservabilityParticipantBucketEnum),
  user_meeting_bucket: z.nativeEnum(GetMeetingObservabilityUserMeetingBucketEnum),
  ax_bucket: z.nativeEnum(GetMeetingObservabilityAxBucketEnum),
  recording_length_bucket: z.nativeEnum(GetMeetingObservabilityRecordingLengthBucketEnum),
}
const version = z
  .string()
  .max(64)
  .regex(/^[a-zA-Z0-9._+-]+$/)
const isFacetDimension = (dimension: AttributionDimension): dimension is (typeof facetDimensions)[number] =>
  (facetDimensions as readonly string[]).includes(dimension)
export const validDimensionValue = (dimension: AttributionDimension, value: string) =>
  (isFacetDimension(dimension) ? version : closedFilters[dimension]).safeParse(value).success

export const filterSchema = z
  .object({
    start: rangeEndpoint,
    end: rangeEndpoint,
    participant_source: z.nativeEnum(GetMeetingObservabilityParticipantSourceEnum),
    negate: z.array(z.enum(attributionDimensions)).max(filterDimensions.length),
    platform: closedFilters.platform.or(z.literal('')),
    os: closedFilters.os.or(z.literal('')),
    client: closedFilters.client.or(z.literal('')),
    calendar_event: closedFilters.calendar_event.or(z.literal('')),
    participant_bucket: closedFilters.participant_bucket.or(z.literal('')),
    user_meeting_bucket: closedFilters.user_meeting_bucket.or(z.literal('')),
    ax_bucket: closedFilters.ax_bucket.or(z.literal('')),
    recording_length_bucket: closedFilters.recording_length_bucket.or(z.literal('')),
    backend_version: version.or(z.literal('')),
    app_version: version.or(z.literal('')),
  })
  .refine(
    (value) =>
      new Set(value.negate).size === value.negate.length && value.negate.every((dimension) => value[dimension]),
    { message: 'Choose one operator for each active filter.', path: ['negate'] }
  )
  .refine((value) => value.start.includes('T') === value.end.includes('T'), {
    message: 'Choose two dates or two timestamps.',
    path: ['end'],
  })
  .refine(
    (value) =>
      value.start.includes('T')
        ? new Date(value.start).getTime() < new Date(value.end).getTime()
        : value.start <= value.end,
    { message: 'End must follow start; calendar dates can be the same day.', path: ['end'] }
  )
  .refine(
    (value) => {
      const duration = new Date(value.end).getTime() - new Date(value.start).getTime()
      return value.start.includes('T') ? duration <= 90 * DAY_MS : duration < 90 * DAY_MS
    },
    {
      message: 'Choose a range of 90 days or fewer.',
      path: ['end'],
    }
  )

export type FilterValues = z.infer<typeof filterSchema>

export const defaultFilterValues = (): FilterValues => {
  const end = new Date().toISOString().slice(0, 10)
  const start = new Date(new Date(end).getTime() - 6 * DAY_MS).toISOString().slice(0, 10)
  return {
    start,
    end,
    participant_source: 'canonical',
    negate: [],
    platform: '',
    os: '',
    client: '',
    calendar_event: '',
    participant_bucket: '',
    user_meeting_bucket: '',
    ax_bucket: '',
    recording_length_bucket: '',
    backend_version: '',
    app_version: '',
  }
}

export const readFilterState = (search: URLSearchParams): { values: FilterValues; notice: string | null } => {
  const defaults = defaultFilterValues()
  const allowed = new Set([...Object.keys(defaults), 'metric', 'tree', 'split'])
  const unexpected = Array.from(search.keys()).some((key) => !allowed.has(key))
  const values = { ...defaults, negate: search.getAll('negate') }
  for (const key of Object.keys(defaults)) {
    if (key === 'negate') continue
    if (search.getAll(key).length > 1) return { values: defaults, notice: 'This saved view has invalid filters.' }
    const value = search.get(key)
    if (value !== null) Object.assign(values, { [key]: value })
  }
  const parsed = filterSchema.safeParse(values)
  if (!parsed.success || unexpected) return { values: defaults, notice: 'This saved view has invalid filters.' }
  return {
    values: { ...parsed.data, negate: filterDimensions.filter((dimension) => parsed.data.negate.includes(dimension)) },
    notice: null,
  }
}

export const readFilterValues = (search: URLSearchParams): FilterValues => readFilterState(search).values

export const filterSearchParams = (values: FilterValues): URLSearchParams => {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(values)) {
    if (key !== 'negate' && value) params.set(key, String(value))
  }
  for (const dimension of filterDimensions)
    if (values.negate.includes(dimension) && values[dimension]) params.append('negate', dimension)
  return params
}

export const toApiFilters = (values: FilterValues): AttributionFilters => {
  const filters: AttributionFilters = {
    start_at: new Date(values.start).toISOString(),
    end_at: new Date(new Date(values.end).getTime() + (values.end.includes('T') ? 0 : DAY_MS)).toISOString(),
    participant_source: values.participant_source,
  }
  for (const dimension of filterDimensions) {
    if (values[dimension]) filters[dimension] = values[dimension]
  }
  const negate = filterDimensions.filter((dimension) => values.negate.includes(dimension) && values[dimension])
  if (negate.length) filters.negate = negate
  return filters
}

export const fixedOptions: Partial<Record<AttributionDimension, string[]>> = Object.fromEntries(
  Object.entries(closedFilters).map(([dimension, schema]) => [
    dimension,
    Object.values(schema.enum).sort(
      (a, b) => Number(['unknown', 'not_applicable'].includes(a)) - Number(['unknown', 'not_applicable'].includes(b))
    ),
  ])
)

export const dimensionValueLabel = (dimension: AttributionDimension, value: string) => {
  if (dimension === 'app_version' && value === OTHER_VERSIONS) return 'Others'
  if (dimension === 'user_meeting_bucket')
    return (
      (
        {
          '1': 'First meeting',
          '2-3': 'Meetings 2–3',
          '4-8': 'Meetings 4–8',
          '9-20': 'Meetings 9–20',
          '21+': 'Meetings 21+',
          unknown: 'Unknown',
        } as Record<string, string>
      )[value] ?? formatValue(value)
    )
  if (dimension === 'recording_length_bucket')
    return (
      (
        {
          '0-5': 'Under 5 min',
          '5-15': '5–15 min',
          '15-30': '15–30 min',
          '30-60': '30–60 min',
          '60-90': '60–90 min',
          '90+': '90+ min',
          unknown: 'Unknown',
        } as Record<string, string>
      )[value] ?? formatValue(value)
    )
  if (dimension === 'calendar_event')
    return (
      ({ none: 'No calendar event', google: 'Google Calendar', outlook: 'Outlook' } as Record<string, string>)[value] ??
      formatValue(value)
    )
  if (dimension === 'ax_bucket' && !['unknown', 'not_applicable'].includes(value))
    return value === '0' ? '0%' : `${value}%`
  return formatValue(value)
}
